// ---------------------------------------------------------------------------
// Screen projection.
//
// The city itself is drawn by Three.js. This is the flat-space statement of the
// same projection, and it exists because the strike replay lays its annotations
// — trails, blasts, dust rings, interceptor streaks — on a 2D canvas over the
// plate, and those have to land on the ground the 3D camera is showing.
// `CityRenderer` builds its camera out of the constants below rather than
// carrying a second copy of the maths, so there is one projection with two
// renderers pointed at it.
//
// A slight turn, not isometric.
//
// The old projection was a classic 2:1 isometric: the grid turned 45°, so the
// city stood on a corner and north, south, east and west were its four points.
// It reads as a diamond, it fills barely half of the rectangle it occupies, and
// the four dead triangles around it were most of the empty space on the pane.
// It then went square on — east right, south down — which filled the pane but
// showed every building from dead ahead, one face and a roof. The camera now
// stands `YAW` round from square on: enough to show two faces of every
// building, little enough that the plate still reads as a block rather than a
// diamond and keeps most of the pane.
//
// Two angles place the camera. `YAW` turns it about the vertical; the ground
// is rotated by it before anything else, so a tile is still TILE_W across in
// the rotated frame. `PITCH` tilts it above the horizon: the ground recedes by
// sin(pitch) while height rises by cos(pitch). Straight down is a perfect
// square with no walls; low is all wall and no ground.
// ---------------------------------------------------------------------------

/** Tile footprint across the screen, in pixels at scale 1. Pitch cannot change it. */
export const TILE_W = 32;

/**
 * Camera yaw, in radians about the vertical: 17.5°.
 *
 * Positive swings the camera from due south towards the east, so the plate
 * turns clockwise on screen: the east edge runs down to the right and the
 * south-east corner is nearest. The camera then sees the south and east faces
 * of every building, the sun (south-west) lights the south ones and leaves the
 * east ones in shade, and the pair of them is what gives a block its depth.
 */
export const YAW = 17.5 * Math.PI / 180;
const COS_YAW = Math.cos(YAW);
const SIN_YAW = Math.sin(YAW);

/**
 * A grid point in the camera's ground frame: `u` across the screen, `v` into
 * it, both in tiles. The rotation is the whole of the yaw.
 */
export function toGround(gx: number, gy: number): { u: number; v: number } {
  return { u: gx * COS_YAW - gy * SIN_YAW, v: gx * SIN_YAW + gy * COS_YAW };
}

/** The inverse: a point in the camera's ground frame back to grid x, y. */
export function fromGround(u: number, v: number): { x: number; y: number } {
  return { x: u * COS_YAW + v * SIN_YAW, y: -u * SIN_YAW + v * COS_YAW };
}

/** The plate grown by `by` tiles on every side, in the ground frame. */
function widened(cw: number, ch: number, by: number): { u0: number; u1: number; v0: number; v1: number } {
  const corners = [toGround(-by, -by), toGround(cw + by, -by), toGround(-by, ch + by), toGround(cw + by, ch + by)];
  return {
    u0: Math.min(...corners.map((c) => c.u)),
    u1: Math.max(...corners.map((c) => c.u)),
    v0: Math.min(...corners.map((c) => c.v)),
    v1: Math.max(...corners.map((c) => c.v)),
  };
}

/** How far a `cw` × `ch` plate runs in the ground frame: its four corners, turned. */
export function plateBounds(cw: number, ch: number): { u0: number; u1: number; v0: number; v1: number } {
  const corners = [toGround(0, 0), toGround(cw, 0), toGround(0, ch), toGround(cw, ch)];
  return {
    u0: Math.min(...corners.map((c) => c.u)),
    u1: Math.max(...corners.map((c) => c.u)),
    v0: Math.min(...corners.map((c) => c.v)),
    v1: Math.max(...corners.map((c) => c.v)),
  };
}

/**
 * Camera pitch, in radians above the horizon.
 *
 * Two values, chosen by the shape of the window rather than a continuum, so a
 * rotation is one discrete switch rather than a re-fit on every resize frame.
 *
 * A square city under a square-on camera is TILE_W across and TILE_W·sin(pitch)
 * deep, so the pitch is also the aspect of the plate: steepen it and the city
 * grows taller and no wider, without a pixel of stretch anywhere. That is the
 * lever a portrait phone wants — the pane is width-bound, so the only way to
 * use the height is to stand the camera up. Landscape gets the shallower angle,
 * which gives the buildings back their walls.
 */
const PITCH_WIDE = 36 * Math.PI / 180;
const PITCH_TALL = 54 * Math.PI / 180;
export let PITCH: number = PITCH_WIDE;

/** Screen depth of one tile, in pixels at scale 1. Derived; never set by hand. */
export let TILE_H: number = TILE_W * Math.sin(PITCH);

/**
 * Screen rise of one world unit of height, in pixels at scale 1.
 *
 * One unit is one tile — the same unit the 3D scene places its models in, so a
 * munition at altitude 2 is two tiles up in both renderers. The old value was a
 * bare 14px against a 32px tile, which quietly drew everything about 30%
 * shorter than the geometry it was annotating.
 */
export let LEVEL: number = TILE_W * Math.cos(PITCH);

/** Height reserved above the plate, in world units, for masts and tall stacks. */
export const HEADROOM = 4;

/**
 * Adopt the projection a window of this shape wants. Returns true if it
 * changed, which means any camera fitted against the old one has to be
 * re-fitted: a view computed at one pitch is centred nowhere at another.
 */
export function adoptProjection(w: number, h: number): boolean {
  const pitch = h > w * 1.15 ? PITCH_TALL : PITCH_WIDE;
  if (pitch === PITCH) return false;
  PITCH = pitch;
  TILE_H = TILE_W * Math.sin(pitch);
  LEVEL = TILE_W * Math.cos(pitch);
  return true;
}

export interface View {
  /** Zoom. 1 means a tile is TILE_W across. */
  scale: number;
  /** Screen-space offset of the grid origin. */
  ox: number;
  oy: number;
}

export interface Pt {
  x: number;
  y: number;
}

/**
 * Grid coordinates to screen. `z` lifts the point off the ground plane. The
 * view's origin is the ground frame's origin, which is the grid's (0, 0).
 */
export function toScreen(gx: number, gy: number, v: View, z = 0): Pt {
  const g = toGround(gx, gy);
  return {
    x: v.ox + g.u * TILE_W * v.scale,
    y: v.oy + g.v * TILE_H * v.scale - z * LEVEL * v.scale,
  };
}

export const clamp = (v: number, lo: number, hi: number): number =>
  (v < lo ? lo : v > hi ? hi : v);

/** A stretch of the ground frame, in tiles: u across the screen, v into it. */
export interface GroundBox {
  u0: number;
  u1: number;
  v0: number;
  v1: number;
}

/**
 * Fit a stretch of ground into a viewport.
 *
 * The box's two spans against the pane's, with headroom added above for art
 * that stands well clear of its footprint. The centring then drops the ground
 * far enough to sit under that headroom rather than through it. `maxScale`
 * holds a small box back from filling the pane, still centred on it.
 */
export function fitBox(W: number, H: number, b: GroundBox, pad = 60, maxScale = Infinity): View {
  const spanX = (b.u1 - b.u0) * TILE_W;
  const spanY = (b.v1 - b.v0) * TILE_H + HEADROOM * LEVEL;
  const scale = Math.min((W - pad * 2) / spanX, (H - pad * 2) / spanY, maxScale);
  return {
    scale,
    ox: W / 2 - spanX * scale / 2 - b.u0 * TILE_W * scale,
    oy: H / 2 - spanY * scale / 2 + HEADROOM * LEVEL * scale - b.v0 * TILE_H * scale,
  };
}

/** Fit a whole city into a viewport. */
export function fitCity(W: number, H: number, cw: number, ch: number, pad = 60): View {
  return fitBox(W, H, plateBounds(cw, ch), pad);
}

/** Screen height of a fitted box, in pixels — what `fitBox` framed. */
export function boxSpanY(b: GroundBox, scale: number): number {
  return ((b.v1 - b.v0) * TILE_H + HEADROOM * LEVEL) * scale;
}

/** Screen height of a fitted city, in pixels — what `fitCity` framed. */
export function citySpanY(cw: number, ch: number, scale: number): number {
  return boxSpanY(plateBounds(cw, ch), scale);
}

/**
 * The stretch of a city a salvo lands on, for the replay to frame.
 *
 * Every point given (aims and impacts, in grid tiles) with `margin` tiles
 * round them, never less than `least` tiles either way, so one round on one
 * roof is not a close-up of a roof, and never past the plate's own box: a
 * frame that would overhang it is slid back in rather than cropped. Points off
 * the plate (a dud that landed outside) are ignored; with none left, the whole
 * plate.
 */
export function strikeBox(
  points: readonly { x: number; y: number }[],
  cw: number,
  ch: number,
  margin = 3,
  least = 14,
  /**
   * How far past the board the frame may run to take in a point: an
   * interception out over the land round the city. Points further out than
   * this are left out of the frame.
   */
  beyond = 0,
): GroundBox {
  const board = plateBounds(cw, ch);
  const wide = beyond > 0 ? widened(cw, ch, beyond) : board;
  const on = points.filter((p) => p.x >= -beyond && p.y >= -beyond && p.x <= cw + beyond && p.y <= ch + beyond);
  // Points on the board frame as they always have; one past the edge widens
  // the frame towards it, but never past the widened plate.
  const plate = on.some((p) => p.x < 0 || p.y < 0 || p.x > cw || p.y > ch) ? wide : board;
  if (on.length === 0) return plate;
  const g = on.map((p) => toGround(p.x, p.y));
  const span = (lo: number, hi: number, min: number, max: number): [number, number] => {
    let a = lo - margin;
    let b = hi + margin;
    if (b - a < least) {
      const mid = (a + b) / 2;
      a = mid - least / 2;
      b = mid + least / 2;
    }
    if (b - a >= max - min) return [min, max];
    if (a < min) return [min, min + (b - a)];
    if (b > max) return [max - (b - a), max];
    return [a, b];
  };
  const [u0, u1] = span(Math.min(...g.map((p) => p.u)), Math.max(...g.map((p) => p.u)), plate.u0, plate.u1);
  const [v0, v1] = span(Math.min(...g.map((p) => p.v)), Math.max(...g.map((p) => p.v)), plate.v0, plate.v1);
  return { u0, u1, v0, v1 };
}
